import React, { useEffect } from 'react';
import { useEditor, EditorContent, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Strike from '@tiptap/extension-strike';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import TextAlign from '@tiptap/extension-text-align';
import TextStyle from '@tiptap/extension-text-style';
import FontSize from '@tiptap/extension-font-size';
import { Box, Button, Divider, Stack, Tooltip, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter';
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatStrikethroughIcon from '@mui/icons-material/FormatStrikethrough';
import FormatSuperscriptIcon from '@mui/icons-material/Superscript';
import FormatSubscriptIcon from '@mui/icons-material/Subscript';

interface TipTapEditorProps {
  value: string;
  onChange: (content: string) => void;
  readOnly: boolean;
  /** When true, content flows in parent scroll (no internal scroll). Use for embedding in EventPage Info tab. */
  embedInScroll?: boolean;
}

// Add these font size options
const fontSizeOptions = [
  { value: '8px', label: 'Tiny' },
  { value: '12px', label: 'Small' },
  { value: '16px', label: 'Normal' },
  { value: '20px', label: 'Large' },
  { value: '24px', label: 'X-Large' },
  { value: '32px', label: 'XX-Large' },
];

const MenuBar = ({ editor }: { editor: Editor | null }) => {
  if (!editor) {
    return null;
  }

  const addLink = () => {
    const url = window.prompt('URL');
    if (url) {
      editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  };

  return (
    <Stack 
      direction="row" 
      spacing={1} 
      sx={{ 
        padding: '8px', 
        borderBottom: '1px solid #e0e0e0',
        flexWrap: 'wrap',
        alignItems: 'center'
      }}
    >
      <Tooltip title="Bold">
        <Button 
          variant={editor.isActive('bold') ? 'contained' : 'outlined'} 
          size="small"
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <FormatBoldIcon fontSize="small" />
        </Button>
      </Tooltip>
      
      <Tooltip title="Italic">
        <Button 
          variant={editor.isActive('italic') ? 'contained' : 'outlined'} 
          size="small"
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <FormatItalicIcon fontSize="small" />
        </Button>
      </Tooltip>
      
      <Tooltip title="Underline">
        <Button 
          variant={editor.isActive('underline') ? 'contained' : 'outlined'} 
          size="small"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <FormatUnderlinedIcon fontSize="small" />
        </Button>
      </Tooltip>
      
      <Tooltip title="Strikethrough">
        <Button 
          variant={editor.isActive('strike') ? 'contained' : 'outlined'} 
          size="small"
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <FormatStrikethroughIcon fontSize="small" />
        </Button>
      </Tooltip>

      <Tooltip title="Superscript">
        <Button 
          variant={editor.isActive('superscript') ? 'contained' : 'outlined'} 
          size="small"
          onClick={() => editor.chain().focus().toggleSuperscript().run()}
        >
          <FormatSuperscriptIcon fontSize="small" />
        </Button>
      </Tooltip>

      <Tooltip title="Subscript">
        <Button 
          variant={editor.isActive('subscript') ? 'contained' : 'outlined'} 
          size="small"
          onClick={() => editor.chain().focus().toggleSubscript().run()}
        >
          <FormatSubscriptIcon fontSize="small" />
        </Button>
      </Tooltip>

      <Divider orientation="vertical" flexItem />
      
      <Tooltip title="Bullet List">
        <Button 
          variant={editor.isActive('bulletList') ? 'contained' : 'outlined'} 
          size="small"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          <FormatListBulletedIcon fontSize="small" />
        </Button>
      </Tooltip>
      
      <Tooltip title="Numbered List">
        <Button 
          variant={editor.isActive('orderedList') ? 'contained' : 'outlined'} 
          size="small"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          <FormatListNumberedIcon fontSize="small" />
        </Button>
      </Tooltip>
      
      <Divider orientation="vertical" flexItem />
      
      <Tooltip title="Align Left">
        <Button 
          variant={editor.isActive({ textAlign: 'left' }) ? 'contained' : 'outlined'} 
          size="small"
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
        >
          <FormatAlignLeftIcon fontSize="small" />
        </Button>
      </Tooltip>
      
      <Tooltip title="Align Center">
        <Button 
          variant={editor.isActive({ textAlign: 'center' }) ? 'contained' : 'outlined'} 
          size="small"
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
        >
          <FormatAlignCenterIcon fontSize="small" />
        </Button>
      </Tooltip>
      
      <Tooltip title="Align Right">
        <Button 
          variant={editor.isActive({ textAlign: 'right' }) ? 'contained' : 'outlined'} 
          size="small"
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
        >
          <FormatAlignRightIcon fontSize="small" />
        </Button>
      </Tooltip>
      
      <Divider orientation="vertical" flexItem />
      
      <Tooltip title="Add Link">
        <Button 
          variant={editor.isActive('link') ? 'contained' : 'outlined'} 
          size="small"
          onClick={addLink}
        >
          <InsertLinkIcon fontSize="small" />
        </Button>
      </Tooltip>
      
      <Divider orientation="vertical" flexItem />
      
      <FormControl size="small" sx={{ minWidth: 100 }}>
        <InputLabel id="font-size-select-label">Font Size</InputLabel>
        <Select
          labelId="font-size-select-label"
          id="font-size-select"
          value={editor.getAttributes('textStyle').fontSize || '16px'}
          label="Font Size"
          onChange={(e) => {
            editor.chain().focus().setMark('textStyle', { fontSize: e.target.value }).run();
          }}
          size="small"
          sx={{
            '& .MuiInputBase-root': {
              padding: '0px 8px',
            },
            '& .MuiSelect-select': {
              fontSize: '0.875rem',
              padding: '4px 8px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.875rem',
              transform: 'translate(14px, 8px) scale(1)',
            },
            '& .MuiInputLabel-shrink': {
              transform: 'translate(14px, -6px) scale(0.75)',
            }
          }}
        >
          {fontSizeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      <Divider orientation="vertical" flexItem />
      
      <Tooltip title="Undo">
        <Button 
          variant="outlined" 
          size="small"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <UndoIcon fontSize="small" />
        </Button>
      </Tooltip>
      
      <Tooltip title="Redo">
        <Button 
          variant="outlined" 
          size="small"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <RedoIcon fontSize="small" />
        </Button>
      </Tooltip>
    </Stack>
  );
};

export const TipTapEditor = ({ value, onChange, readOnly, embedInScroll = false }: TipTapEditorProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Strike,
      Superscript,
      Subscript,
      FontSize.configure({
        types: ['textStyle'],
      }),
      Link.configure({
        openOnClick: true,
        linkOnPaste: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
        alignments: ['left', 'center', 'right'],
      }),
    ],
    content: value,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && editor.isEditable !== !readOnly) {
      editor.setEditable(!readOnly);
    }
  }, [readOnly, editor]);

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [value, editor]);

  const contentBoxSx = embedInScroll
    ? {
        padding: '16px',
        '& .ProseMirror': {
          outline: 'none',
          minHeight: '1.5em',
          textAlign: 'left',
          fontFamily: (theme: any) => theme.typography.fontFamily,
          fontSize: (theme: any) => theme.typography.fontSize,
          color: (theme: any) => theme.palette.text.primary,
          '& p': { margin: '0 0 0.5em 0' },
          '& ul, & ol': { margin: '0 0 0.5em 0', paddingLeft: '1.5em' },
          '& [style*="text-align:center"]': { textAlign: 'center' },
          '& [style*="text-align:right"]': { textAlign: 'right' },
        },
        '& .ProseMirror-focused': {
          outline: 'none',
        },
      }
    : {
        flex: 1,
        overflow: 'auto',
        padding: '16px',
        '& .ProseMirror': {
          outline: 'none',
          height: '100%',
          textAlign: 'left',
          fontFamily: (theme: any) => theme.typography.fontFamily,
          fontSize: (theme: any) => theme.typography.fontSize,
          color: (theme: any) => theme.palette.text.primary,
          '& p': { margin: '0 0 0.5em 0' },
          '& ul, & ol': { margin: '0 0 0.5em 0', paddingLeft: '1.5em' },
          '& [style*="text-align:center"]': { textAlign: 'center' },
          '& [style*="text-align:right"]': { textAlign: 'right' },
        },
        '& .ProseMirror-focused': {
          outline: 'none',
        },
      };

  return (
    <Box
      sx={
        embedInScroll
          ? {
              borderRadius: '4px',
              display: 'flex',
              flexDirection: 'column',
            }
          : {
              borderRadius: '4px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }
      }
    >
      {!readOnly && <MenuBar editor={editor} />}
      <Box sx={contentBoxSx}>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};